import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import crossword, { CrosswordField } from './data';
import useChainInput from './use-chain-input';
import './style.scss';
import useTask from '../../../../modules/tasks/hooks/use-task';
import QuestionBox from '../../../question-box';

type Props = {
    onComplete?: () => void
}

const QUESTIONS = [
    'Wróżka, przyjaciółka Piotrusia Pana.',
    'Imię bohaterki wiersza Jana Brzechwy p.t. „Katar”.',
    'Nazwisko poetki, która uwielbiała jeść fast foody.',
    'Nazwisko doktora, który rozumiał mowę zwierząt.',
    'Choroba, na którą zapadła Nel Rawlison w książce p.t.: „W pustyni i w puszczy”.',
    'Rodzaj książki, w której opisane są np. dobre nawyki.',
    'Imię dziewczynki, która potrafiła podnieść konia.',
    'Państwo, w którym biuro detektywistyczne prowadzą Tiril i Oliver, bohaterowie serii książek „Operacja”.',
    'Najmądrzejsza w całym Stumilowym Lesie.',
    'Miejsce w którym pierwszy raz zgubił się Czarny Nosek.',
    'Brat Hani Humorek.',
    'Imię przyjaciółki Felixa i Neta.',
    'Czesała się nią Kaczka dziwaczka.',
    'Olej, który dawnej podawało się dzieciom, żeby były zdrowe.',
    'Pan Kleks nagradzał nimi pilnych uczniów.',
    '… grochu. Przez nie Księżniczka nie mogła się wyspać.',
]

const Task306: React.FC<Props> = ({ onComplete: completeCallback }) => {

    const containerRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(0);
    const crosswordColumnCount = 14;
    const crosswordRowCount = 16;
    const cellSize = useMemo(() => {
        return containerWidth / crosswordColumnCount;
    }, [containerWidth])

    const { registerContainer, registerInput } = useChainInput();

    const onResize = useCallback(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.getBoundingClientRect().width);
        }
    }, [setContainerWidth, containerRef]);

    const validate = useCallback(() => {
        if (containerRef.current) {
            const inputs = containerRef.current.querySelectorAll('input');

            const correct = Array.from(inputs)
                .reduce((prev, next) => {
                    return prev && next.dataset['correct'] === next.value.toLowerCase();
                }, true);

            if (correct && completeCallback) {
                completeCallback()
            }

        }
    }, [containerRef, completeCallback]);

    const [showCorrect, setShowCorrect] = useState(false);

    useEffect(() => {
        onResize();
        window.addEventListener('resize', onResize);

        return () => {
            window.removeEventListener('resize', onResize);
        }
    }, [onResize]);

    const [question, setQuestion] = useState<string | null>(null);

    const { task } = useTask('t-306');

    return <div className="task task-306">
        <h2>zadanie 6</h2>
        <h3>{task?.title}</h3>
        <p>
            Są książki dzieciństwa, których się nie zapomina. Wróćcie wspólnie do dziecięcej krainy i udowodnijcie, że znacie ją najlepiej na świecie!
        </p>
        <p className="instruction">Zadanie: Rozwiążcie krzyżówkę.</p>

        <div ref={registerContainer}>
            <div className="cw-container" ref={containerRef} style={{ height: `${cellSize * crosswordRowCount}px` }}>
                {
                    crossword.map((field, index) => {

                        return <Cell
                            key={index}
                            cellSize={cellSize}
                            field={field}
                            inputRef={field.chain ? registerInput(field?.chain) : null}
                            onChange={validate}
                            showCorrect={showCorrect}
                            onFocus={() => setQuestion(QUESTIONS[field.position.y])}
                            onBlur={() => setQuestion(null)}
                        />
                    })
                }
            </div>
        </div>

        <div className="check">
            <button className="button" onClick={() => setShowCorrect(true)}>Sprawdź</button>
        </div>

        <QuestionBox>
            <p>
                Jak już jestesmy przy temacie wspomnień, czy pamiętacie słowa, które tworzyliście jako małe dzieci?<br /> Spróbujcie swoich sił w grze <a rel="noreferrer" href="https://gra.ceo.org.pl" target="_blank"><strong>DoMowy</strong></a>.
            </p>
        </QuestionBox>

        <div className={`question ${question ? 'opened' : 'closed'}`}>{question}</div>

    </div>
}

type CellProps = {
    cellSize: number,
    field: CrosswordField,
    inputRef?: any
    onChange: () => void,
    onFocus: () => void,
    onBlur: () => void,
    showCorrect: boolean
}

const Cell: React.FC<CellProps> = ({ cellSize, field, inputRef, onChange, showCorrect, onFocus, onBlur }) => {
    const borderWidth = 3;

    const [correct, setCorrect] = useState(false);
    const onValueChange = useCallback((e: React.FormEvent<HTMLInputElement>) => {
        setCorrect((e.currentTarget.value[e.currentTarget.value.length - 1] || '').toLowerCase() === field.correct);

        if (onChange) {
            onChange();
        }
    }, [setCorrect, field, onChange]);


    return <div className={`cw-cell cell-${field.type} ${field.extraClass || ''} ${showCorrect ? (correct ? 'correct' : 'wrong') : ''} ${field.marked ? 'marked' : ''}`} style={{
        transform: `translate(${(cellSize) * field.position.x}px, ${(cellSize) * field.position.y}px)`,
        width: `${cellSize - borderWidth}px`,
        height: `${cellSize - borderWidth}px`,
        borderWidth: `${borderWidth}px`
    }}>
        {field.pencil && <div className='pencil-mark'>{field.pencil}</div>}
        {(field.type === 'label') && <span style={{ fontSize: `${cellSize * .75}px` }}>{field.text}</span>}
        {(field.type === 'input') && field.chain && <input
            type="text"
            data-correct={field.correct}
            ref={inputRef}
            style={{ fontSize: `${cellSize * .75}px` }}
            onFocus={(e: React.FormEvent<HTMLInputElement>) => {
                const target = e.currentTarget;
                setTimeout(() => {
                    target.setSelectionRange(1, 1);
                }, 50);
                onFocus();
            }}
            onBlur={onBlur}
            onInput={onValueChange}
        />}
    </div>
}

export default Task306;
